import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Paper, Skeleton, Stack, Tooltip, Typography } from '@mui/material'

import { SUPPLIER_ACTIVITY_RECENCY_CHART_HEIGHT } from 'components/reports/Metrics/constants/SupplierActivityRecency'
import { SupplierActivityRecencyMetricDto } from 'generated/graphql'

import { RecencyCumulativeChart } from './RecencyCumulativeChart'

interface Props {
  title: string
  subtitle: string
  info: string
  xAxisLabel: string
  eligibleCaption: string
  outsideWindowCaption: string
  color: string
  metric?: SupplierActivityRecencyMetricDto | null
  networkSupplierCount?: number
  loading?: boolean
}

export const RecencyCumulativeCard = ({
  title,
  subtitle,
  info,
  xAxisLabel,
  eligibleCaption,
  outsideWindowCaption,
  color,
  metric,
  networkSupplierCount,
  loading,
}: Props) => (
  <Paper
    variant='outlined'
    sx={{ p: 2.5, height: '100%', borderRadius: 2, borderTop: `3px solid ${color}` }}>
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      justifyContent='space-between'
      alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
      mb={2}>
      <Box>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography variant='subtitle1' fontWeight={700} sx={{ color }}>
            {title}
          </Typography>
          <Tooltip title={info}>
            <InfoOutlinedIcon fontSize='small' sx={{ color: 'text.disabled' }} />
          </Tooltip>
        </Stack>
        <Typography variant='body2' color='primary.main'>
          {subtitle}
        </Typography>
      </Box>

      <Stack alignItems={{ xs: 'flex-start', sm: 'flex-end' }} spacing={0.5}>
        <Box textAlign={{ xs: 'left', sm: 'right' }}>
          <Typography variant='caption' color='text.secondary' display='block'>
            {eligibleCaption}
          </Typography>
          {loading ? (
            <Skeleton width={60} height={32} />
          ) : (
            <Typography variant='h6' fontWeight={700}>
              {(metric?.eligibleCount ?? 0).toLocaleString()}
            </Typography>
          )}
        </Box>
        <Typography variant='caption' color='text.disabled'>
          of {(networkSupplierCount ?? 0).toLocaleString()} in the network
        </Typography>
      </Stack>
    </Stack>

    {loading ? (
      <Skeleton
        variant='rectangular'
        height={SUPPLIER_ACTIVITY_RECENCY_CHART_HEIGHT}
        sx={{ borderRadius: 1 }}
      />
    ) : (
      <RecencyCumulativeChart points={metric?.points ?? []} color={color} xAxisLabel={xAxisLabel} />
    )}

    <Stack
      direction='row'
      justifyContent='space-between'
      alignItems='center'
      mt={2}
      p={1.5}
      sx={{ backgroundColor: 'action.hover', borderRadius: 1 }}>
      <Typography variant='body2' color='text.secondary'>
        {outsideWindowCaption}
      </Typography>
      {loading ? (
        <Skeleton width={90} />
      ) : (
        <Typography variant='subtitle1' fontWeight={700}>
          {(metric?.outsideWindowCount ?? 0).toLocaleString()} (
          {metric?.outsideWindowPercentage ?? 0}%)
        </Typography>
      )}
    </Stack>
  </Paper>
)
